import React, { useCallback, useEffect, useState } from 'react';
import {
  disconnectPartnerIntegration,
  getPartnerIntegrationStatus,
  PartnerIntegrationStatus,
  rotatePartnerWebhookSecret,
  updatePartnerIntegration,
} from '../../api/integrations';
import { ReferralCodeLegend } from '../ReferralCodeLegend';
import { toast } from 'sonner';
import LoadingComponent from '../LoadingComponent';
import {
  FULFILLMENT_MODE_OPTIONS,
  PARTNER_INTEGRATION_STATE_LABELS,
  formatContractState,
  getAiriWheelsSignupUrlFromEnv,
  getAiriWheelsWebhookUrlFromEnv,
} from '../../utils/fundingPartners';
import { FieldLabelWithTooltip } from '../FieldLabelWithTooltip';
import { FundingFulfillmentMode, PartnerIntegrationStatus as PartnerIntegrationState } from '../../data/types';

type FinancialPartnerIntegrationProps = {
  communityId: string
  userId: string
  onClose?: () => void
  /** Called after a successful save/disconnect so the Settings card can refresh Connected status. */
  onStatusChange?: () => void
}

/** QuickBooks-style connection card for AiriWheels and future financial partners. */
const FinancialPartnerIntegration: React.FC<FinancialPartnerIntegrationProps> = ({
  communityId,
  onClose,
  onStatusChange,
}) => {
  const [status, setStatus] = useState<PartnerIntegrationStatus | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [signupUrl, setSignupUrl] = useState('');
  const [fulfillmentMode, setFulfillmentMode] = useState<FundingFulfillmentMode>('api_handoff');
  const [resourceId, setResourceId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [secret, setSecret] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const integrationStatus = await getPartnerIntegrationStatus(communityId, 'airiwheels');
      setStatus(integrationStatus);
      setWebhookUrl(integrationStatus.webhook_url || getAiriWheelsWebhookUrlFromEnv() || '');
      setSignupUrl(
        (integrationStatus as PartnerIntegrationStatus & { signup_url?: string }).signup_url
          || getAiriWheelsSignupUrlFromEnv()
          || ''
      );
      setFulfillmentMode(
        ((integrationStatus as PartnerIntegrationStatus & { fulfillment_mode?: FundingFulfillmentMode }).fulfillment_mode
          || 'api_handoff') as FundingFulfillmentMode
      );
      setResourceId(integrationStatus.resource_id || '');
    } catch (error) {
      console.error('Error loading AiriWheels status:', error);
      toast.error('Failed to load AiriWheels integration status');
    } finally {
      setIsLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSave = async () => {
    try {
      const updated = await updatePartnerIntegration(communityId, 'airiwheels', {
        webhook_url: webhookUrl,
        signup_url: signupUrl,
        fulfillment_mode: fulfillmentMode,
        resource_id: resourceId,
        status: webhookUrl ? 'connected' : 'disconnected',
        auth_mode: 'hmac_webhook',
        capabilities: ['deep_link', 'outbound_webhook', 'inbound_webhook'],
      });
      setStatus(updated);
      toast.success(
        webhookUrl
          ? 'AiriWheels webhook connected'
          : 'AiriWheels disconnected; referrals will wait until AiriWheels is connected again.'
      );
      onStatusChange?.();
    } catch (error) {
      toast.error('Failed to update AiriWheels connection');
    }
  };

  const handleRotate = async () => {
    try {
      const updated = await rotatePartnerWebhookSecret(communityId, 'airiwheels');
      const { webhook_secret: oneTimeSecret, ...safeStatus } = updated;
      setStatus(safeStatus);
      setSecret(oneTimeSecret || '');
      toast.success('Webhook secret rotated. Copy it now.');
    } catch (error) {
      toast.error('Failed to rotate webhook secret');
    }
  };

  const handleDisconnect = async (
    nextState: Extract<PartnerIntegrationState, 'disconnected' | 'revoked'>
  ) => {
    try {
      const updated = await disconnectPartnerIntegration(communityId, 'airiwheels', nextState);
      setStatus(updated);
      setSecret('');
      toast.success(
        nextState === 'revoked'
          ? 'AiriWheels access revoked; future sync is stopped'
          : 'AiriWheels disconnected; referrals will wait until AiriWheels is connected again.'
      );
      onStatusChange?.();
    } catch (error) {
      toast.error(`Failed to ${nextState === 'revoked' ? 'revoke' : 'disconnect'} AiriWheels`);
    }
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  const integrationState: PartnerIntegrationState =
    status?.status || (status?.connected ? 'connected' : 'disconnected');
  const connected = integrationState === 'connected';
  const integrationId = status?.integration_id || status?.id || 'Not provisioned';
  const stateBadgeClass = connected
    ? 'bg-[#E7F5EC] text-[#0F973D]'
    : integrationState === 'revoked' || integrationState === 'error'
      ? 'bg-[#FEE2E2] text-[#B91C1C]'
      : 'bg-primary-tint text-primary-100';

  return (
    <div className="flex flex-col font-open-sans" data-testid="airiwheels-integration">
      <p className="page-kicker">Financial partners</p>
      <h2 className="font-raleway text-xl font-bold text-ink mt-2 mb-1">AiriWheels</h2>
      <p className="text-sm text-[#3F5B64] mb-4">
        Connect AiriWheels so HomeCook can send consented referrals to their signup link and webhook.
        Signup URL, webhook URL, and fulfillment mode stay in sync with Partner setup on the bound listing.
        Public product copy (wheels, eligibility, pricing) is edited on Listing &amp; eligibility, not here.
      </p>
      <div className="partner-section-card p-4 mb-4">
        <p className="text-sm text-ink">
          Status:{' '}
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-raleway font-semibold ${stateBadgeClass}`}>
            {formatContractState(integrationState, PARTNER_INTEGRATION_STATE_LABELS)}
          </span>
        </p>
        <p className="text-sm text-muted mt-2">Tenant: {communityId}</p>
        <p className="text-sm text-muted mt-1" data-testid="partner-integration-id">
          Integration ID: {integrationId}
        </p>
        {status?.error && <p className="text-sm text-red-500 mt-2">{status.error}</p>}
        {status?.last_synced_at && <p className="text-sm text-muted mt-2">Last sync: {status.last_synced_at}</p>}
      </div>
      <p className="page-kicker mb-2">Funding resource</p>
      <FieldLabelWithTooltip
        label="Bound funding resource ID"
        tooltip="Inbound AiriWheels referrals attach to this funding resource. Required before inbound referrals are accepted. Secrets stay on the server."
      />
      <input
        className="auth-input mb-3"
        data-testid="partner-integration-resource-id"
        value={resourceId}
        onChange={(e) => setResourceId(e.target.value)}
        placeholder="Funding resource UUID for inbound AiriWheels referrals"
      />
      <p className="mb-3 text-xs text-muted">Required before inbound partner referrals can be accepted for this community.</p>
      <FieldLabelWithTooltip
        label="Signup URL"
        tooltip="Partner destination members open after a consented intro. Synced with Partner setup on the bound listing."
      />
      <input
        className="auth-input mb-3"
        data-testid="partner-integration-signup-url"
        value={signupUrl}
        onChange={(e) => setSignupUrl(e.target.value)}
        placeholder="https://partner.example/signup"
      />
      <FieldLabelWithTooltip
        label="Fulfillment mode"
        tooltip="Same field as Listing & eligibility / Partner setup on the bound listing."
      />
      <select
        className="auth-input mb-3"
        data-testid="partner-integration-fulfillment-mode"
        value={fulfillmentMode}
        onChange={(e) => setFulfillmentMode(e.target.value as FundingFulfillmentMode)}
      >
        {FULFILLMENT_MODE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <label className="text-sm font-raleway text-ink mb-1">Partner webhook URL</label>
      <input
        className="auth-input mb-3"
        data-testid="partner-integration-webhook-url"
        value={webhookUrl}
        onChange={(e) => setWebhookUrl(e.target.value)}
        placeholder="https://partner.example/webhooks/homecook"
      />
      <label className="text-sm font-raleway text-ink mb-1">HomeCook inbound webhook</label>
      <input className="auth-input mb-3 bg-primary-tint" readOnly value={status?.homecook_webhook_url || ''} />
      <p className="mb-3 text-xs text-muted">
        Launch authentication uses server-to-server HMAC-signed webhooks. OAuth is a future option.
        Channel codes use <ReferralCodeLegend />.
      </p>
      {secret && (
        <div className="bg-primary-tint p-3 rounded-lg mb-3 text-ink" role="status">
          <p className="text-xs font-semibold">New HMAC secret — shown only for this rotation</p>
          <code className="mt-1 block break-all text-xs" data-testid="one-time-partner-secret">{secret}</code>
          <button
            type="button"
            className="mt-2 text-xs font-raleway font-semibold text-primary-300"
            onClick={() => setSecret('')}
          >
            Clear secret
          </button>
        </div>
      )}
      <p className="mb-3 text-xs text-muted">
        Partner secrets stay on backend systems and are never stored in browser storage. Revocation stops future sync; it does not erase retained records.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="inline-flex h-12 items-center rounded-lg bg-primaryDark-0 px-5 font-raleway text-sm font-semibold text-white hover:bg-primary-300" onClick={handleSave}>Save connection</button>
        <button type="button" className="inline-flex h-12 items-center rounded-lg border border-primary-400 px-5 font-raleway text-sm font-semibold text-primary-300" onClick={handleRotate}>Rotate secret</button>
        {connected && (
          <>
            <button type="button" className="inline-flex h-12 items-center rounded-lg border border-[#D5E3E8] px-5 font-raleway text-sm font-semibold text-ink" onClick={() => handleDisconnect('disconnected')}>Disconnect</button>
            <button type="button" className="inline-flex h-12 items-center rounded-lg border border-red-200 px-5 font-raleway text-sm font-semibold text-red-700" onClick={() => handleDisconnect('revoked')}>Revoke access</button>
          </>
        )}
        <span className="inline-flex h-12 items-center px-2 text-xs text-muted">OAuth: future</span>
        {onClose && <button type="button" className="text-muted font-raleway text-sm" onClick={onClose}>Close</button>}
      </div>
    </div>
  );
};

export default FinancialPartnerIntegration;
